import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useStyles, lightTheme } from "../../assets/styles/Theme.tsx";
import { Person, Lock, AccountCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { useDispatch } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";

// API and store imports
import { LOGIN_API } from "../../api/ApiModel.tsx";
import { setUserParams } from "../../store/ReduxUser.tsx";
import { externalpostApi } from "../../services/externalApis.tsx";
import { CON_MODULECODE, CON_MODULEKEY } from "../../utils/sys/Constants.tsx";
import { setAccessRights } from "../../store/ReduxAccessRight.tsx";
import { LOGIN_FAILED, LOGIN_SUCCESS } from "../../utils/sys/Messages.tsx";
import { setAccessModules } from "../../store/ReduxAccessModules.tsx";
import { setmoduleParamJson } from "../../store/ReduxModuleParam.tsx";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Image imports using require to avoid TypeScript declaration issues
const login_bg_1: string = require("../../assets/images/login_bg_1.jpg");
const login_bg_2: string = require("../../assets/images/login_bg_2.jpg");
const login_bg_3: string = require("../../assets/images/login_bg_3.jpg");
const login_bg_4: string = require("../../assets/images/login_bg_4.jpg");
const login_bg_5: string = require("../../assets/images/login_bg_5.jpg");
const pos_2: string = require("../../assets/images/pos_2.png");
const pos_1: string = require("../../assets/images/pos_1.png");

// Types and Interfaces
interface LoginResponse {
  value?: {
    user?: any;
    accessRights?: any;
    accessModules?: any;
    moduleParamJson?: any;
  };
  user?: any;
  accessRights?: any;
  accessModules?: any;
  moduleParamJson?: any;
}

interface LoginError {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

// Extended theme interface
interface CustomTheme {
  palette: {
    text: {
      icons?: string;
      primary: string;
      secondary: string;
    };
  };
}

const Login: React.FC = () => {
  // Hooks start
  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning" | "info">("success");
  // Hooks end

  // Handlers start
  const handleCloseSnackbar = (): void => {
    setSnackbarOpen(false);
  };

const handleLogin = async (): Promise<void> => {
  try {
    // Clear previous error
    setError("");

    const loginResponse = (await externalpostApi(LOGIN_API, {
      username,
      password,
      appModule: CON_MODULECODE,
      appToken: CON_MODULEKEY,
    })) as LoginResponse;

    console.log("loginResponse", loginResponse);

    // Handle both response formats
    const responseData = loginResponse?.value || loginResponse;

    if (!responseData || !responseData.user) {
      throw new Error(LOGIN_FAILED);
    }

    const {
      user,
      accessRights,
      accessModules,
      moduleParamJson,
    } = responseData;

    // Dispatch safely
    dispatch(setUserParams(user));
    if (accessRights) dispatch(setAccessRights(accessRights));
    if (accessModules) dispatch(setAccessModules(accessModules));
    if (moduleParamJson) dispatch(setmoduleParamJson(moduleParamJson));

    // Success UI
    setSnackbarMessage(LOGIN_SUCCESS);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);

    // Navigate AFTER slight delay (so snackbar is visible)
    setTimeout(() => {
      navigate("/dashboard");
    }, 700);

  } catch (err) {
    const loginError = err as LoginError;

    console.error("Login failed:", loginError);

    // Extract real backend message if exists
    const errorMessage =
      loginError?.response?.data?.message ||
      loginError?.message ||
      "Login failed. Please check your credentials and try again.";

    // Update UI
    setError(errorMessage);
    setSnackbarMessage(errorMessage);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  }
};

  // Custom Image Slider Component
  const ImageSlider: React.FC = () => {
    const images = [
      login_bg_1,
      login_bg_2,
      login_bg_3,
      login_bg_4,
      login_bg_5,
    ];
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex: number) => (prevIndex + 1) % images.length);
      }, 3000);

      return () => clearInterval(interval);
    }, [images.length]);

    return (
      <Box
        component="img"
        src={images[currentImageIndex]}
        alt="Point of Order System"
        sx={{
          transition: "opacity 1s ease-in-out",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: 2,
          position: "relative",
        }}
      />
    );
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box sx={styles.login_container}>
        {/* Background Shape Animation */}
        <Box sx={styles.shape1}>
          <Box
            component="img"
            src={pos_2}
            alt="Point of Order System"
            sx={styles.img_lg_card_shape1}
          />
        </Box>
        <Box sx={styles.shape2}>
          <Box
            component="img"
            src={pos_1}
            alt="Point of Order System"
            sx={styles.img_lg_card_shape1}
          />
        </Box>
        <Box zIndex={2} width={"auto"}>
          <Grid container sx={styles.login_card_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
              sx={styles.login_card_left}
            >
              <ImageSlider />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
              sx={styles.login_card_right}
            >
              <Typography variant="h3" sx={styles.retro_text}>
                Hello,
              </Typography>

              <Typography variant="h6" sx={styles.retro_text}>
                Welcome Back!
              </Typography>

              <Box
                component="form"
                sx={{
                  "& > :not(style)": { my: 3 },
                }}
                noValidate
                autoComplete="off"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <AccountCircle
                    sx={{ color: theme.palette.text.icons || '#666', mr: 1, my: 0.5 }}
                  />
                  <TextField
                    id="txtUsername"
                    label="Username"
                    variant="outlined"
                    fullWidth
                    size="small"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Lock
                    sx={{ color: theme.palette.text.icons || '#666', mr: 1, my: 0.5 }}
                  />
                  <TextField
                    id="txtPassword"
                    label="Password"
                    size="small"
                    type="password"
                    variant="outlined"
                    fullWidth
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  />
                </Box>
              </Box>

              <Stack
                spacing={2}
                direction="row"
                mt={4}
                justifyContent={"flex-end"}
              >
                <Button
                  fullWidth
                  onClick={handleLogin}
                  variant="contained"
                  size="medium"
                  sx={styles.login_btn}
                >
                  Log In
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Box textAlign={"right"} width={"100%"}>
            <Typography variant="body2" sx={styles.login_retro_text}>
              Item Management System
            </Typography>
          </Box>
        </Box>
        <SystemAlert
          open={snackbarOpen}
          onClose={handleCloseSnackbar}
          message={snackbarMessage}
          type={snackbarSeverity}
        />
      </Box>
    </ThemeProvider>
  );
};

export default Login;